/**
 * Concrete implementation of MapLayerBase which serves as a wrapper around ESRI feature layers.
 */
import MapLayerBase from "app/models/map/base/MapLayerBase";
import _ from "underscore";
import $ from "jquery";
import Field from "core/models/Field";
import MapQuery from "app/models/map/MapQuery";
import GeoUtils from "app/utils/GeoUtils";
import s from "underscore.string";
import UrlBuilder from "@HBKEngineering/url-builder";
import Backbone from "backbone";
import moment from "moment";
import async from "async";
import ErrorUtils from "app/utils/ErrorUtils";
import messenger from "app/Messenger";
import ExternalFeature from "../ExternalFeature";

var registerVisibility = function (self, layer) {
  layer.on("visibility-change", function (evt) {
    self.setVisible(evt.visible);
  });
};

export default MapLayerBase.extend({
  /**
   * Initializes this object.
   * @param  {object} options The options hash.
   * @param {Array} [options.children] The list of child feature layers of this layer.
   * @param {string} [options.url] The URL from which to load this layer. Either this or options.children is required.
   * @param {FeatureLayer} [options.parent] The parent of this FeatureLayer.
   * @param {esri/layers/LayerInfo} options.info The feature layer info, returned from the ArcGIS server.
   * @param {app/models/map/ExternalMapService} options.serviceLayer The service layer associated with this layer.
   */
  initialize: function (options) {
    this.displayOnPan = $.browser && $.browser.msie ? false : true;
    /**
     * The feature layer info.
     * @type {esri/layers/LayerInfo}
     */
    this.info = options.info;
    /**
     * The service layer associated with this layer.
     * @type {app/models/map/ExternalMapService}
     */
    this.serviceLayer = options.serviceLayer;

    /**
     * The parent layer, if any.
     * @type {FeatureLayer}
     */
    this.parent = options.parent;

    this.registerVisibility = _.once(registerVisibility);

    if (_.isArray(options.children)) {
      this._children = options.children.slice();

      _.each(
        options.children,
        function (childLayer) {
          childLayer.parent = this;
        },
        this
      );

      this._layer = null;
    } else if (_.isString(options.url)) {
      var FeatureLayer = window.require("esri/layers/FeatureLayer");

      this._children = [];

      /**
       *    From Esri Documentation, concerning displayOnPan: When true, graphics are displayed during panning. When false, the graphics are turned off during pan movement. Setting to false may improve performance in Internet Explorer.
       */

      this._layer = new FeatureLayer(options.url, {
        mode: options.snapshot
          ? FeatureLayer.MODE_SNAPSHOT
          : FeatureLayer.MODE_ONDEMAND,
        outFields: ["*"],
        visible: options.visible && options.serviceLayer.visible,
        displayOnPan: this.displayOnPan,
      });

      this._initializeLayer(this._layer, options);
    }
  },

  /**
   * A promise which resolves when the layer has initially loaded and rejects with errors.
   */
  _layerLoading: null,

  /**
   * Initializes the layer associated with this instance, given options.
   * @param {FeatureLayer} featureLayer The ESRI FeatureLayer instance to initialize
   * @param {Object} options The hash of options for initialization.
   * @private
   */
  _initializeLayer: function (featureLayer, options) {
    var layerLoading = new $.Deferred();
    var self = this;

    this._layerLoading = layerLoading;
    featureLayer.showLabels = !!options.serviceLayer.service.get("showLabels");
    featureLayer.on("load", function () {
      layerLoading.resolve(self, featureLayer);
    });
    featureLayer.on("error", function (err) {
      layerLoading.reject(err, self, featureLayer);
    });

    if (options.snapshot) {
      var mapQuery = new MapQuery(this);
      var maxRecords =
        options.serviceLayer.maxRecordCount ||
        options.serviceLayer.getMaxRecordCount();

      //TODO investigate why clicking on a graphic removes it. But for now just add graphic back when removed. We dont want to be removing external graphics anyway.
      featureLayer.on("graphic-remove", function (evt) {
        featureLayer.add(evt.graphic);
      });

      featureLayer._mode.startup = function () {};

      mapQuery
        .loadAll(maxRecords)
        .fail(function (err, featureLayer, featuresProcessed, totalFeatures) {
          var errorMessage =
            "loading the features for " +
            featureLayer.name +
            ". Loaded " +
            featuresProcessed +
            " out of " +
            totalFeatures +
            " total features.";
          var normalizedError = ErrorUtils.normalizeError(
            {
              activity: errorMessage,
            },
            err
          );

          messenger.error(normalizedError.userMessage);
        });
    }
  },

  load: function () {
    var deferred = new $.Deferred();

    if (this._layerLoading) {
      this._layerLoading
        .done(function (self, layer) {
          layer.info = self.info;
          layer.serviceLayer = self.serviceLayer;

          self.registerVisibility(self, layer);
          self.setVisible(self.visible); //this is a weird thing to do

          if (
            layer.labelingInfo &&
            layer.labelingInfo.length &&
            _.isObject(layer.labelingInfo[0])
          ) {
            layer.labelingInfo[0].symbol.setAngle(-45);
            layer.labelingInfo[0].labelPlacement = "above-right";
          }

          deferred.resolve(self, layer);
        })
        .fail(function (err, self, layer) {
          deferred.reject(err, self, layer);
        });
    } else {
      deferred.resolve(this, this.layer);
    }

    return deferred.promise();
  },

  getChildren: function () {
    return this._children;
  },

  getMapLayer: function () {
    return this._layer;
  },

  getFields: function () {
    if (!this._fields && this.layer) {
      var linkTemplate = _.template(
        '<a href="<%-value%>" target="_blank"><%-value%></a>'
      );

      this._fields = _.map(
        _.filter(this.layer.fields, function (layerField) {
          return _.contains(
            [
              "esriFieldTypeSmallInteger",
              "esriFieldTypeInteger",
              "esriFieldTypeSingle",
              "esriFieldTypeDouble",
              "esriFieldTypeString",
              "esriFieldTypeDate",
            ],
            layerField.type
          );
        }),
        function (layerField) {
          var ViewerView = {
            template: function (options) {
              var value = options.value;
              var result = value;

              if (UrlBuilder.isUri(value)) {
                result = linkTemplate(options);
              }

              return result;
            },

            getControlValue: function () {
              return this.$el.data("value");
            },

            setControlValue: function (value) {
              this.$el.data("value", value);
            },
          };

          layerField.filterable = true;
          if (layerField.type == "esriFieldTypeDate") {
            var translateEsriDate = function (value) {
              var result = "Never";
              var nearestDay;

              if (value) {
                value = moment(value);
                nearestDay = moment(value).startOf("day");

                if (+value == +nearestDay) {
                  //if there is no real time component, just show the date
                  result = value.format("M/DD/YYYY");
                } else {
                  result = value.format("M/DD/YYYY h:mm a");
                }
              }

              return result;
            };

            ViewerView = {
              tagName: "span",

              template: function (options) {
                return translateEsriDate(options.value);
              },

              getControlValue: function () {
                return this.$el.data("value");
              },

              setControlValue: function (value) {
                this.$el.data("value", value);
                this.render();
              },
            };
            layerField.filterable = function (value) {
              return translateEsriDate(this.getValue(value));
            };
          }

          return new Field(
            {
              id: layerField.name,
              label: s(layerField.alias || layerField.name)
                .humanize()
                .titleize()
                .value(),
              name: layerField.name,
              editor: "custom",
              filterable: layerField.filterable,
              ViewerView: ViewerView,
              esriType: layerField.type,
            },
            {
              getter: function (model) {
                if (model) {
                  if (model instanceof Backbone.Model) {
                    var attrs = model.get("attributes") || model.attributes;
                    var val;

                    if (attrs) {
                      val = attrs[layerField.name];
                    }

                    return val || "";
                  } else if (_.isObject(model)) {
                    return model.attributes[layerField.name] || "";
                  }
                } else {
                  return "";
                }
              },
            }
          );
        }
      );
    }

    return this._fields;
  },

  getId: function (model) {
    return this.toGraphic(model).attributes[this.layer.objectIdField];
  },

  getIdField: function () {
    if (!this._idField) {
      var self = this;

      this._idField = new Field(
        {
          name: this.layer.objectIdField,
          id: this.layer.objectIdField,
          label: "ID",
        },
        {
          getter: function (model) {
            return self.toGraphic(model).attributes[this.get("name")];
          },
          setter: function (model, value) {
            self.toGraphic(model).attributes[this.get("name")] = value;
          },
        }
      );
    }

    return this._idField;
  },

  getDisplayName: function (model) {
    if (!this.layer) {
      return "";
    }

    return this.toGraphic(model).attributes[this.layer.displayField];
  },

  getDisplayNameField: function () {
    if (!this._displayField && this.layer) {
      var self = this;

      this._displayField = new Field(
        {
          id: this.layer.displayField,
          name: this.layer.displayField,
          label: s.humanize(this.layer.displayField),
        },
        {
          getter: function (model) {
            return self.toGraphic(model).attributes[this.get("name")];
          },
          setter: function (model, value) {
            self.toGraphic(model).attributes[this.get("name")] = value;
          },
        }
      );
    }

    return this._displayField;
  },

  getExtent: function () {
    if (this.layer) {
      return this.layer.fullExtent;
    } else {
      return GeoUtils.unionExtents(this.children);
    }
  },

  findModelsFromLabel: function (label, point) {
    if (label && label.getLayer() == this.getMapLayer()) {
      var model = this.toFeature(label);

      model.originalLayer = this;

      return [model];
    } else {
      return [];
    }
  },

  highlight: function (symbolFactory, selector) {
    //selector is array of features in this layer, or a single feature.
    var self = this;

    if (!_.isArray(selector)) {
      selector = [selector];
    }

    var query = new MapQuery(this);

    _.each(selector, function (feature) {
      query.ids(self.getId(feature));
    });

    return query.highlight(symbolFactory);
  },

  unhighlight: function (selector) {
    var self = this;

    if (selector) {
      if (!_.isArray(selector)) {
        selector = [selector];
      }

      var query = new MapQuery(this);

      _.each(selector, function (feature) {
        query.ids(self.getId(feature));
      });

      return query.unhighlight();
    } else {
      if (this.layer && this.layer.getSelectedFeatures().length) {
        return this.layer.clearSelection();
      } else {
        return $.when();
      }
    }
  },

  loadModel: function (modelId) {
    var query = new MapQuery(this);
    var deferred = new $.Deferred();

    query.ids(modelId);

    query
      .execute()
      .done(function (results) {
        deferred.resolve(this.toFeature(_.first(results)));
      })
      .fail(function (err) {
        deferred.reject(err);
      });

    return deferred.promise();
  },

  hasFiles: function () {
    return this.serviceLayer.service.get("directoryKey") ? true : false;
  },

  toFeature: function (model) {
    if (model._feature) {
      return model._feature;
    } else if (model instanceof ExternalFeature) {
      return model;
    } else if (_.isFunction(model.getFeature)) {
      return model.getFeature();
    } else {
      return new ExternalFeature(model, {
        layer: this,
      });
    }
  },

  toData: function (model) {
    return this.toFeature(model).getProperties();
  },

  toGeometry: function (model) {
    return this.toFeature(model).getGeometry();
  },

  toGraphic: function (model) {
    return model.graphic || model;
  },

  getRootFilePath: function (model) {
    return this.hasFiles() && this.toFeature(model).getDocumentsUrl();
  },

  getChildDirectoryPath: function (folderModel) {
    return this.hasFiles() ? folderModel.id : null;
  },

  getChildFilePath: function (fileModel) {
    return this.hasFiles() ? fileModel.id : null;
  },

  getWrappedModel: function () {
    return this.info;
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/FeatureLayer.js
// module id = 511
// module chunks = 1